import React, {useCallback, useEffect, useMemo, useState} from 'react';

// styles
import {
  Content,
  IconBack,
  InputLabel,
  FormControls,
  FormContent,
  FormInput,
  TitleContent,
  StyledButton,
} from '../LoginPage-styles';
import {LinkPrimary, Subtitle, TitleExtraLarge} from '../../../shared/styles';

// components
import {FormControl, FormHelperText, Input} from '@mui/joy';

// hooks
import {useMutation} from '@tanstack/react-query';

// helpers
import {otpLogin, validateOtp} from '../../../shared/services/auth';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {checkIsEmailAddress, formatPhoneNumber} from '../../../shared/helpers';

// lib
import {AxiosError} from 'axios';

// services
import {CreditBookApi} from '../../../shared/services/ApiService';
import {Auth} from '../../../shared/services/ApiService/types/endpoints';

interface OneTimePasswordFormProps {
  username: string;
  onSubmit: () => void;
  onReturn: () => void;
  handleOnPending: (isPending: boolean) => void;
}

const OneTimePasswordForm = (props: OneTimePasswordFormProps) => {
  const {t} = useTranslation();

  const {username, onSubmit, onReturn, handleOnPending} = props;

  const [otp, setOtp] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasError, setHasError] = useState(false);

  const {isPending: validateIsPending, mutate: validate} = useMutation<
    void,
    AxiosError<{errorCode: string; errorMessage: string}>
  >({
    mutationFn: () => otpLogin(username, otp),
    onSuccess: onSubmit,
    onError: (err) => {
      toast.error(
        err?.response?.data.errorMessage ?? t('global_errorOccurred'),
      );
      setHasError(true);
    },
  });

  /**
   * Handle OTP change
   */
  const handleOtpChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setOtp(value);
      if (validateOtp(value)) {
        setErrorMessage('');
        setHasError(false);
      } else {
        setErrorMessage(t('login_otp_input_error'));
        setHasError(true);
      }
    },
    [setOtp, setHasError, setErrorMessage, t],
  );

  const {isPending: resendPasswordIsPending, mutate: resendPassword} =
    useMutation({
      mutationFn: (name: string) => CreditBookApi.auth.authenticate(name),
      onSuccess: (data: Auth) => {
        toast.success(data.data.message, {
          autoClose: 10000,
        });
      },
      onError: () => {
        toast.error(t('global_errorOccurred'));
      },
    });

  // Update the isPending state
  useEffect(() => {
    handleOnPending(resendPasswordIsPending || validateIsPending);
  }, [handleOnPending, resendPasswordIsPending, validateIsPending]);

  /**
   * Handle submit and validate OTP
   * If the OTP is valid, call the validate mutation
   * Otherwise, set an error message
   */
  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (hasError || errorMessage) {
        return;
      }

      if (!validateOtp(otp)) {
        setErrorMessage(t('login_otp_input_error'));
        return;
      }

      validate();
    },
    [validate, otp, t, hasError, errorMessage],
  );

  const isEmailAddress = useMemo(
    () => checkIsEmailAddress(username),
    [username],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Content>
        <FormContent>
          <TitleContent>
            <TitleExtraLarge>{t('login_otp_title')}</TitleExtraLarge>
            <Subtitle>
              {t('login_password_sent_message', {
                username: isEmailAddress
                  ? username
                  : formatPhoneNumber(username),
              })}
            </Subtitle>
          </TitleContent>
          <FormInput>
            <FormControl error={!!errorMessage}>
              <InputLabel bold="bold">{t('login_code_placeholder')}</InputLabel>
              <Input
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder={t('login_code_placeholder')}
                autoFocus
              />
              <FormHelperText>{errorMessage}</FormHelperText>
            </FormControl>
            <LinkPrimary
              disabled={resendPasswordIsPending || validateIsPending}
              onClick={() => resendPassword(username)}>
              {t('login_request_new_code_button')}
            </LinkPrimary>
          </FormInput>
        </FormContent>
        <FormControls>
          <StyledButton
            type="submit"
            size="lg"
            fullWidth
            disabled={
              otp.trim().length < 1 || resendPasswordIsPending || hasError
            }
            loading={validateIsPending}>
            {t('login_confirm_button')}
          </StyledButton>
          <StyledButton
            size="lg"
            onClick={onReturn}
            fullWidth
            disabled={resendPasswordIsPending || validateIsPending}
            variant="outlined"
            startDecorator={<IconBack />}>
            {t('login_return_button')}
          </StyledButton>
        </FormControls>
      </Content>
    </form>
  );
};

export default OneTimePasswordForm;
